import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import MaterialUtils from '../helpers/MaterialUtils';
export default class Walls {
    constructor(scene, wallMaterial) {
        this.scene = scene;
        // Load the texture for the top face
        const textureLoader = new THREE.TextureLoader();
        this.bumpMap = textureLoader.load('/textures/stone-wall-texture.jpg');
        this.topTexture = textureLoader.load('/textures/oak_veneer_01_diff_4k.jpg');

        // Create the wood material for the top face using MaterialUtils
        this.topMaterial = MaterialUtils.createWoodMaterial('/textures/oak_veneer_01_diff_4k.jpg');
        // Add spiral landing loading
        
        this.spiralLanding = null;
        
        // Create walls initially
        this.createWalls();
        this.ready = Promise.resolve();
    }
    async loadSpiralLanding(type) {
        const loader = new GLTFLoader();
        const landingPath = type === 'Basic-Spiral' 
            ? 'glTFModels/Spiral-Landing-dbl.gltf'
            : 'glTFModels/Bespoke-Spiral-Landing.gltf';
    
        try {
            console.log("Loading spiral landing for:", type);
            const gltf = await new Promise((resolve, reject) => {
                loader.load(
                    landingPath,
                    (gltf) => resolve(gltf),
                    undefined,
                    (error) => reject(error)
                );
            });
            
            if (gltf && gltf.scene) {
                this.spiralLanding = gltf.scene;
                this.spiralLanding.name = 'SpiralLanding';
                this.spiralLanding.visible = false;
                this.scene.add(this.spiralLanding);
                console.log("Spiral landing loaded successfully:", this.spiralLanding);
                return this.spiralLanding; // Return the landing
            }
            throw new Error('GLTF loading completed but scene was not available');
        } catch (error) {
            console.error('Error loading spiral landing:', error);
            this.spiralLanding = null;
            throw error; // Re-throw to handle in calling code
        }
    }

    // Method to create the walls
    createWalls() {
        this.removeExistingWalls();
        
        // Create separate materials for each wall
       
        // Materials: White for all faces except the top face, which has the texture
        const whiteMaterial = new THREE.MeshStandardMaterial({
            color: 0xf0f0f0,
            roughness: 0.8,  // Higher roughness for matte painted wall
            metalness: 0.05,    // Non-metallic surface
           bumpMap: this.bumpMap, // Add subtle bump map for texture
           bumpScale: 1  // Keep the bump map subtle
        });
        //const topMaterial = new THREE.MeshBasicMaterial({ map: this.topTexture });

        const wallMaterials = [
            whiteMaterial,  // Right face
            whiteMaterial,  // Left face
            this.topMaterial,    // Top face (with texture)
            whiteMaterial,  // Bottom face
            whiteMaterial,  // Front face
            whiteMaterial   // Back face
        ];

        const cantileverMaterial = new THREE.MeshStandardMaterial({
            color: 0xf0f0f0,
            roughness: 0.8,  // Higher roughness for matte painted wall
            metalness: 0.05,    // Non-metallic surface
           bumpMap: this.bumpMap, // Add subtle bump map for texture
           bumpScale: 1,  // Keep the bump map subtle
           transparent: true,
           opacity: 0.5,
           side: THREE.BackSide
        });
        // Create the back wall
        this.backWall = new THREE.Mesh(
            new THREE.BoxGeometry(8, 5, 0.2),
            wallMaterials
        );
        this.backWall.receiveShadow = true;
        this.backWall.castShadow = true;
        this.backWall.name = 'BackWall';
        this.scene.add(this.backWall);
        // Dynamically adjust texture repeat for the top face
        console.log('createWalls BackWall dimensions:', this.backWall.geometry.parameters);

        // Create the side wall
        this.sideWall = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 5, 10),
            wallMaterials
        );
        this.sideWall.receiveShadow = true;
        this.sideWall.castShadow = true;
        this.sideWall.name = 'SideWall';
        this.scene.add(this.sideWall);
        console.log('createWalls SideWall dimensions:', this.sideWall.geometry.parameters);

        // Create the additional wall (but keep it invisible for now)
        this.additionalWall = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 5, 8),
            wallMaterials
        );
        this.additionalWall.receiveShadow = true;
        this.additionalWall.castShadow = true;
        this.additionalWall.name = 'AdditionalWall';
        this.additionalWall.visible = false;  // Initially hidden
        this.scene.add(this.additionalWall);
        console.log('createWalls AdditionalWall dimensions:', this.additionalWall.geometry.parameters);

        this.cantileverWall = new THREE.Mesh(
            new THREE.BoxGeometry(0.01, 1, 1), // Thin along x-axis, adjustable height and depth
            cantileverMaterial
        );
        this.cantileverWall.receiveShadow = true;
        this.cantileverWall.castShadow = true;
        this.cantileverWall.name = 'cantileverWall';
        this.cantileverWall.visible = false;  // Initially hidden
        this.scene.add(this.cantileverWall);
    
    }

    // Remove any existing walls to avoid duplicates
    removeExistingWalls() {
        const backWall = this.scene.getObjectByName('BackWall');
        const sideWall = this.scene.getObjectByName('SideWall');
        const additionalWall = this.scene.getObjectByName('AdditionalWall');
        const cantileverWall = this.scene.getObjectByName('cantileverWall');

        if (backWall) {
            this.scene.remove(backWall);
        }
        if (sideWall) {
            this.scene.remove(sideWall);
        }
        if (additionalWall) {
            this.scene.remove(additionalWall);
        }
        if (cantileverWall) {
            this.scene.remove(cantileverWall);
        }
    }

    // Method to update the wall dimensions and positioning based on the top tread
    updateWalls(topTreadYOffset, topTreadZOffset, stairType, totalBackWallWidth = 8.4) {
        const wallHeight = 0.75;  // Height of the back wall
        const wallThickness = 5;  // Thickness of the wall
        const sideWallThickness = 3;  // Side wall thickness
        
        // Adjust back wall width and position
        const newBackWallWidth = totalBackWallWidth;
        this.backWall.geometry.dispose();  // Dispose of the old geometry
        this.backWall.geometry = new THREE.BoxGeometry(newBackWallWidth, wallHeight, wallThickness);  // Update width
    
        let zPosition;
        if (stairType === 'U-shaped') {
            // Adjust the back wall position independently using a custom offset
            const backWallInitialPosition = -2.8;  // You can fine-tune this value
            zPosition = topTreadZOffset + backWallInitialPosition;  // This offset controls only the back wall's Z-position
        } else {
            // Default positioning for all other stair types (Straight, L-shaped, etc.)
            const backWallOffset = stairType === 'L-shaped' ? -5.8 : -5.2;  // Offset for L-shaped stairs, else default
            zPosition = topTreadZOffset - wallThickness / 2 - backWallOffset;
        }
    
        // Set back wall position (this should now only affect the back wall, not the side wall)
        this.backWall.position.set(
            sideWallThickness + (newBackWallWidth / 2) - (sideWallThickness * 2.4),  // Shift to the left by half the new width
            topTreadYOffset + wallHeight / 2,
            zPosition  // Z-position changes based on stair type
        );
    
        // Adjust the side wall length based on stair type
        let sideWallLength;
    
        if (stairType === 'U-shaped') {
            // Special side wall length for U-shaped stairs
            const customSideWallLength = 12;  // You can adjust this value to set the Z-axis length for the side wall in U-shaped stairs
            sideWallLength = customSideWallLength;
        } else {
            // Default length for other stair types
            sideWallLength = Math.abs(topTreadZOffset) + 3;
        }
    
        if (stairType === 'Spiral') {
            this.sideWall.visible = false;
            this.backWall.visible = false;
        }
        // Update side wall dimensions and position
        const sideWallHeight = topTreadYOffset + 1.125;  // Adjusted based on top tread position
        this.sideWall.geometry.dispose();
        this.sideWall.geometry = new THREE.BoxGeometry(sideWallThickness, sideWallHeight, sideWallLength);
        this.sideWall.position.set(
            -2.7, 
            sideWallHeight / 2 - 0.75 / 2, 
            topTreadZOffset / 2  // This still uses topTreadZOffset to maintain side wall behavior
        );
    
        // Show/hide the additional wall for L-shaped stairs
        if (stairType === 'L-shaped') {
            this.additionalWall.visible = true;
            this.additionalWall.geometry.dispose();
            this.additionalWall.geometry = new THREE.BoxGeometry(sideWallThickness - 0.2, wallHeight, 3);  // Adjusted for L-shaped stairs
            this.additionalWall.position.set(
                totalBackWallWidth - (sideWallThickness * 1.865),
                topTreadYOffset + wallHeight / 2,
                topTreadZOffset  // Align with back wall
            );
        } else {
            this.additionalWall.visible = false;
        }
    }                
}
